// ---------------------------------------------------------------------------
// C87: Community Schema — 7 tables for community spaces, posts, gamification
// ---------------------------------------------------------------------------
import {
  pgTable,
  text,
  timestamp,
  uuid,
  integer,
  boolean,
  jsonb,
  index,
  uniqueIndex,
  check,
} from "drizzle-orm/pg-core";
import { sql } from "drizzle-orm";

const tz = { withTimezone: true } as const;

// ---------------------------------------------------------------------------
// Community spaces (sub-groups within a tenant)
// ---------------------------------------------------------------------------

export const SPACE_ACCESS_TYPES = ["free", "paid", "tiered"] as const;
export type SpaceAccessType = (typeof SPACE_ACCESS_TYPES)[number];

export const communitySpaces = pgTable(
  "community_spaces",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    tenantId: uuid("tenant_id").notNull(),
    name: text("name").notNull(),
    slug: text("slug").notNull(),
    description: text("description"),
    coverImage: text("cover_image"),
    accessType: text("access_type").notNull().default("free"),
    stripePriceId: text("stripe_price_id"),
    settings: jsonb("settings").$type<Record<string, unknown>>().default({}),
    memberCount: integer("member_count").notNull().default(0),
    postCount: integer("post_count").notNull().default(0),
    isArchived: boolean("is_archived").notNull().default(false),
    createdBy: text("created_by").notNull(),
    createdAt: timestamp("created_at", tz).notNull().defaultNow(),
    updatedAt: timestamp("updated_at", tz).notNull().defaultNow(),
  },
  (table) => [
    uniqueIndex("idx_community_spaces_slug").on(table.tenantId, table.slug),
    index("idx_community_spaces_tenant").on(table.tenantId),
    check(
      "chk_space_access_type",
      sql`${table.accessType} IN ('free', 'paid', 'tiered')`,
    ),
  ],
);

// ---------------------------------------------------------------------------
// Community memberships
// ---------------------------------------------------------------------------

export const MEMBERSHIP_ROLES = ["member", "moderator", "admin"] as const;
export type MembershipRole = (typeof MEMBERSHIP_ROLES)[number];

export const communityMemberships = pgTable(
  "community_memberships",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    tenantId: uuid("tenant_id").notNull(),
    spaceId: uuid("space_id")
      .notNull()
      .references(() => communitySpaces.id, { onDelete: "cascade" }),
    userId: text("user_id").notNull(),
    role: text("role").notNull().default("member"),
    level: integer("level").notNull().default(1),
    points: integer("points").notNull().default(0),
    stripeSubscriptionId: text("stripe_subscription_id"),
    joinedAt: timestamp("joined_at", tz).notNull().defaultNow(),
    lastActiveAt: timestamp("last_active_at", tz),
  },
  (table) => [
    uniqueIndex("idx_community_membership_unique").on(table.spaceId, table.userId),
    index("idx_community_membership_tenant").on(table.tenantId),
    index("idx_community_membership_points").on(table.spaceId, table.points),
    check(
      "chk_membership_role",
      sql`${table.role} IN ('member', 'moderator', 'admin')`,
    ),
  ],
);

// ---------------------------------------------------------------------------
// Community posts (5 types)
// ---------------------------------------------------------------------------

export const POST_TYPES = ["post", "poll", "question", "milestone", "announcement"] as const;
export type CommunityPostType = (typeof POST_TYPES)[number];

export const communityPosts = pgTable(
  "community_posts",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    tenantId: uuid("tenant_id").notNull(),
    spaceId: uuid("space_id")
      .notNull()
      .references(() => communitySpaces.id, { onDelete: "cascade" }),
    authorId: text("author_id").notNull(),
    authorName: text("author_name").notNull(),
    postType: text("post_type").notNull().default("post"),
    title: text("title"),
    content: text("content").notNull(),
    mediaUrls: jsonb("media_urls").$type<string[]>().default([]),
    pollOptions: jsonb("poll_options").$type<Array<{ id: string; text: string; votes: number }>>(),
    aiClassification: text("ai_classification"),
    isPinned: boolean("is_pinned").notNull().default(false),
    isLocked: boolean("is_locked").notNull().default(false),
    commentCount: integer("comment_count").notNull().default(0),
    reactionCount: integer("reaction_count").notNull().default(0),
    createdAt: timestamp("created_at", tz).notNull().defaultNow(),
    updatedAt: timestamp("updated_at", tz).notNull().defaultNow(),
  },
  (table) => [
    index("idx_community_posts_space").on(table.spaceId, table.createdAt),
    index("idx_community_posts_tenant").on(table.tenantId),
    index("idx_community_posts_pinned").on(table.spaceId, table.isPinned),
    check(
      "chk_post_type",
      sql`${table.postType} IN ('post', 'poll', 'question', 'milestone', 'announcement')`,
    ),
  ],
);

// ---------------------------------------------------------------------------
// Community comments (threaded)
// ---------------------------------------------------------------------------

export const communityComments = pgTable(
  "community_comments",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    tenantId: uuid("tenant_id").notNull(),
    postId: uuid("post_id")
      .notNull()
      .references(() => communityPosts.id, { onDelete: "cascade" }),
    parentId: uuid("parent_id"),
    authorId: text("author_id").notNull(),
    authorName: text("author_name").notNull(),
    content: text("content").notNull(),
    likesCount: integer("likes_count").notNull().default(0),
    createdAt: timestamp("created_at", tz).notNull().defaultNow(),
    updatedAt: timestamp("updated_at", tz).notNull().defaultNow(),
  },
  (table) => [
    index("idx_community_comments_post").on(table.postId, table.createdAt),
    index("idx_community_comments_parent").on(table.parentId),
  ],
);

// ---------------------------------------------------------------------------
// Community reactions (like/love/fire/celebrate — one per user per target)
// ---------------------------------------------------------------------------

export const REACTION_TYPES = ["like", "love", "fire", "celebrate"] as const;
export type ReactionType = (typeof REACTION_TYPES)[number];

export const communityReactions = pgTable(
  "community_reactions",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    tenantId: uuid("tenant_id").notNull(),
    targetType: text("target_type").notNull(),
    targetId: uuid("target_id").notNull(),
    userId: text("user_id").notNull(),
    reactionType: text("reaction_type").notNull(),
    createdAt: timestamp("created_at", tz).notNull().defaultNow(),
  },
  (table) => [
    uniqueIndex("idx_community_reaction_unique").on(table.targetType, table.targetId, table.userId),
    index("idx_community_reactions_target").on(table.targetType, table.targetId),
    check(
      "chk_reaction_type",
      sql`${table.reactionType} IN ('like', 'love', 'fire', 'celebrate')`,
    ),
    check(
      "chk_reaction_target_type",
      sql`${table.targetType} IN ('post', 'comment')`,
    ),
  ],
);

// ---------------------------------------------------------------------------
// CCW-I: Space-scoped member bans
// ---------------------------------------------------------------------------
// CCW-D (#788) flagged: no member-ban path scoped to a single space. A
// space moderator/admin must be able to block an abusive member from one
// space without removing them from the whole tenant. Bans are filtered at
// post/reply create time — we never mutate prior content.
//
// Tenant scoping: every query AND-s on tenantId. CQRS: ban/unban events
// emitted from the route so audit captures who blocked whom and when.

export const communityMemberBans = pgTable(
  "community_member_bans",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    tenantId: uuid("tenant_id").notNull(),
    spaceId: uuid("space_id")
      .notNull()
      .references(() => communitySpaces.id, { onDelete: "cascade" }),
    // Banned user id — auth users live outside the tenant DB so we keep
    // this as free-form text (matches `community_memberships.user_id`).
    userId: text("user_id").notNull(),
    reason: text("reason").notNull(),
    // Captured at ban time. No FK across tenant DB boundary.
    bannedBy: text("banned_by").notNull(),
    bannedAt: timestamp("banned_at", tz).notNull().defaultNow(),
    // Nullable = permanent. Setting an expiry lets a cleanup job reinstate
    // the member automatically once the cooling-off window passes.
    expiresAt: timestamp("expires_at", tz),
    createdAt: timestamp("created_at", tz).notNull().defaultNow(),
  },
  (table) => [
    // One active ban per (tenant, space, user). Re-banning replaces by
    // DELETE-then-INSERT rather than collecting duplicates.
    uniqueIndex("uq_community_member_bans_tenant_space_user")
      .on(table.tenantId, table.spaceId, table.userId),
    index("idx_community_member_bans_tenant").on(table.tenantId),
    index("idx_community_member_bans_space").on(table.spaceId),
    // Cleanup job filter — sweep expired bans by tenant.
    index("idx_community_member_bans_expires").on(table.tenantId, table.expiresAt),
  ],
);

// ---------------------------------------------------------------------------
// Community badges (auto-award based on criteria)
// ---------------------------------------------------------------------------

export const communityBadges = pgTable(
  "community_badges",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    tenantId: uuid("tenant_id").notNull(),
    spaceId: uuid("space_id")
      .references(() => communitySpaces.id, { onDelete: "cascade" }),
    name: text("name").notNull(),
    description: text("description"),
    iconUrl: text("icon_url"),
    awardCriteria: jsonb("award_criteria")
      .$type<{ type: string; threshold: number; metric: string }>()
      .notNull(),
    pointsValue: integer("points_value").notNull().default(25),
    isActive: boolean("is_active").notNull().default(true),
    createdAt: timestamp("created_at", tz).notNull().defaultNow(),
  },
  (table) => [
    index("idx_community_badges_tenant").on(table.tenantId),
    index("idx_community_badges_space").on(table.spaceId),
  ],
);

// ---------------------------------------------------------------------------
// Community badge awards
// ---------------------------------------------------------------------------

export const communityBadgeAwards = pgTable(
  "community_badge_awards",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    tenantId: uuid("tenant_id").notNull(),
    badgeId: uuid("badge_id")
      .notNull()
      .references(() => communityBadges.id, { onDelete: "cascade" }),
    userId: text("user_id").notNull(),
    awardedAt: timestamp("awarded_at", tz).notNull().defaultNow(),
  },
  (table) => [
    uniqueIndex("idx_community_badge_award_unique").on(table.badgeId, table.userId),
    index("idx_community_badge_awards_user").on(table.userId),
  ],
);
